/**
 * Region Service
 * 区域管理 API 服务（只读）
 * 区域数据通过 Alembic 迁移从 CSV 导入，仅提供查询接口
 */
import api, {
    MutationRequestOptions,
    getMutationRequestConfig,
} from '../utils/axiosConfig'
import { normalizeSearchText } from '../utils/searchText'

// ========== 类型定义 ==========

export interface RegionSimple {
    id: number
    name: string
    code: string
    level: number
    parent_id: number | null
    is_custom: boolean
    is_active: boolean
    path_label?: string | null
}

export interface Region extends RegionSimple {
    path: string
    is_custom: boolean
    is_active: boolean
    created_at: string
    updated_at: string
    children: RegionSimple[]
}

export interface RegionTreeNode {
    id: number
    name: string
    code: string
    level: number
    parent_id: number | null
    is_custom: boolean
    children: RegionTreeNode[]
}

export interface RegionStats {
    total: number
    levels: Array<{
        level: number
        name: string
        count: number
    }>
}

export interface RegionUserSummary {
    id: string
    username: string
    region_id?: number | null
    is_active: boolean
}

export interface RegionDeviceSummary {
    id: string
    name?: string | null
    address?: string | null
    status: number
    region_id?: number | null
    last_active_at?: string | null
}

export type RegionQueryMode = 'annotation' | 'history' | 'review_export'

// ========== 区域查询 API ==========

/**
 * 获取区域列表
 */
export const getRegions = async (params?: {
    skip?: number
    limit?: number
    level?: number
    min_level?: number
    parent_id?: number
    is_active?: boolean
    include_inactive?: boolean
    leaf_only?: boolean
    keyword?: string
}): Promise<RegionSimple[]> => {
    const response = await api.get<RegionSimple[]>('/regions', { params })
    return response.data
}

/**
 * 获取区域树形结构
 */
export const getRegionTree = async (maxLevel: number = 3): Promise<RegionTreeNode[]> => {
    const response = await api.get<{ regions: RegionTreeNode[] }>('/regions/tree', {
        params: { max_level: maxLevel }
    })
    return response.data.regions
}

/**
 * 获取所有省份
 */
export const getProvinces = async (): Promise<RegionSimple[]> => {
    const response = await api.get<RegionSimple[]>('/regions/provinces')
    return response.data
}

/**
 * 获取子区域（用于四级联动选择器）
 */
export const getChildren = async (regionId: number): Promise<RegionSimple[]> => {
    const response = await api.get<RegionSimple[]>(`/regions/children/${regionId}`)
    return response.data
}

/**
 * 搜索区域
 */
export const searchRegions = async (
    keyword: string,
    options?: {
        level?: number
        minLevel?: number
        leafOnly?: boolean
        limit?: number
        mode?: RegionQueryMode
        includeInactive?: boolean
    }
): Promise<RegionSimple[]> => {
    const response = await api.get<RegionSimple[]>('/regions/search', {
        params: {
            keyword: normalizeSearchText(keyword),
            level: options?.level,
            min_level: options?.minLevel,
            leaf_only: options?.leafOnly,
            limit: options?.limit ?? 100,
            mode: options?.mode,
            include_inactive: options?.includeInactive,
        }
    })
    return response.data
}

/**
 * 获取区域详情（含子区域）
 */
export const getRegion = async (regionId: number): Promise<Region> => {
    const response = await api.get<Region>(`/regions/${regionId}`)
    return response.data
}

/**
 * 获取区域路径（从根到当前区域）
 */
export const getRegionPath = async (
    regionId: number,
    options?: {
        mode?: RegionQueryMode
    }
): Promise<RegionSimple[]> => {
    const response = await api.get<RegionSimple[]>(`/regions/${regionId}/path`, {
        params: {
            mode: options?.mode,
        },
    })
    return response.data
}

/**
 * 获取区域统计信息
 */
export const getRegionStats = async (): Promise<RegionStats> => {
    const response = await api.get<RegionStats>('/regions/count')
    return response.data
}

/**
 * 获取区域下的用户
 */
export const getRegionUsers = async (regionId: number): Promise<RegionUserSummary[]> => {
    const response = await api.get<RegionUserSummary[]>(`/regions/${regionId}/users`)
    return response.data
}

/**
 * 获取区域下的设备
 */
export const getRegionDevices = async (regionId: number): Promise<RegionDeviceSummary[]> => {
    const response = await api.get<RegionDeviceSummary[]>(`/regions/${regionId}/devices`)
    return response.data
}

export interface CreateCustomRegionRequest {
    name: string
    parent_id: number
}

export interface UpdateCustomRegionRequest {
    name?: string
    is_active?: boolean
}

export const createCustomRegion = async (
    data: CreateCustomRegionRequest,
    options?: MutationRequestOptions
): Promise<Region> => {
    const response = await api.post<Region>(
        '/regions',
        data,
        getMutationRequestConfig(options)
    )
    return response.data
}

export const updateCustomRegion = async (
    regionId: number,
    data: UpdateCustomRegionRequest,
    options?: MutationRequestOptions
): Promise<Region> => {
    const response = await api.patch<Region>(
        `/regions/${regionId}`,
        data,
        getMutationRequestConfig(options)
    )
    return response.data
}

export const deleteCustomRegion = async (
    regionId: number,
    options?: MutationRequestOptions
): Promise<void> => {
    await api.delete(`/regions/${regionId}`, getMutationRequestConfig(options))
}
